import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const _API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";
const UPLOADS_BASE = _API_BASE.startsWith("/") ? "" : _API_BASE;

/* ─── Display maps ──────────────────────────────────────────────────────────── */
const GENDER_LABELS = {
  male: "Male", female: "Female",
  non_binary: "Non-binary", prefer_not_to_say: "Prefer not to say",
};

const WORK_TYPE_LABELS = {
  desk_work: "Desk Work", field_work: "Field Work", physical_labour: "Physical Labour",
  mixed: "Mixed", retired: "Retired", student: "Student",
};

const DIET_LABELS = {
  omnivore: "Omnivore", vegetarian: "Vegetarian", vegan: "Vegan",
  pescatarian: "Pescatarian", keto: "Keto", paleo: "Paleo", other: "Other",
};

const SLEEP_QUALITY_LABELS = {
  poor: "Poor", fair: "Fair", good: "Good", excellent: "Excellent",
};

const EXERCISE_FREQ_LABELS = {
  none: "None", "1x_week": "1× / week", "2x_week": "2× / week",
  "3x_week": "3× / week", "4x_week": "4× / week", daily: "Daily",
};

const STRESS_LABELS = {
  low: "Low", moderate: "Moderate", high: "High", very_high: "Very High",
};

const ALCOHOL_LABELS = {
  none: "None", occasional: "Occasional", moderate: "Moderate", heavy: "Heavy",
};

const SMOKING_LABELS = {
  never: "Never", ex_smoker: "Ex-Smoker", current: "Current",
};

const HORMONAL_LABELS = {
  regular_cycles: "Regular Cycles", irregular: "Irregular",
  perimenopause: "Perimenopause", menopause: "Menopause",
  post_menopause: "Post-Menopause", on_hrt: "On HRT", not_applicable: "Not Applicable",
};

const CLUSTER_COLORS = {
  cardiovascular:  { color: "#DC2626" },
  metabolic:       { color: "#EA580C" },
  hormonal:        { color: "#7C3AED" },
  neurological:    { color: "#1D4ED8" },
  musculoskeletal: { color: "#16A34A" },
  immune:          { color: "#B45309" },
  longevity:       { color: "#5A90A8" },
};

/* ─── Shared styles ─────────────────────────────────────────────────────────── */
const card = {
  background: "#FFFFFF",
  border: "1px solid rgba(90,144,168,0.18)",
  borderRadius: 20,
  padding: "26px 28px",
  boxShadow: "0 2px 16px rgba(90,144,168,0.1)",
};
const sectionHead = {
  fontSize: 10, fontWeight: 700, letterSpacing: "0.12em",
  textTransform: "uppercase", color: "#5A90A8",
  fontFamily: "Inter, sans-serif", marginBottom: 18,
  paddingBottom: 14, borderBottom: "1px solid rgba(90,144,168,0.15)",
};
const rowLabel = {
  fontSize: 12, color: "#7AAABB",
  fontFamily: "Inter, sans-serif", flexShrink: 0, minWidth: "min(130px, 35vw)",
};
const rowValue = {
  fontSize: 13, color: "#1C3545",
  fontFamily: "Inter, sans-serif", textAlign: "right", lineHeight: 1.55,
  wordBreak: "break-word",
};

/* ─── Small components ──────────────────────────────────────────────────────── */
function Tag({ children, color }) {
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px", borderRadius: 999,
      background: color ? `${color}18` : "rgba(90,144,168,0.1)",
      border: `1px solid ${color ? `${color}35` : "rgba(90,144,168,0.22)"}`,
      color: color || "#5A90A8",
      fontSize: 12, fontFamily: "Inter, sans-serif", margin: "2px 3px 2px 0",
    }}>
      {children}
    </span>
  );
}

function InfoRow({ label, value, tags }) {
  const empty = tags ? !tags?.length : (value === null || value === undefined || value === "");
  if (empty) return null;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: tags ? "flex-start" : "center", gap: 16, padding: "9px 0", borderBottom: "1px solid rgba(90,144,168,0.08)" }}>
      <span style={rowLabel}>{label}</span>
      {tags
        ? <div style={{ textAlign: "right" }}>{tags.map((t, i) => <Tag key={i}>{t}</Tag>)}</div>
        : <span style={rowValue}>{value}</span>
      }
    </div>
  );
}

function EmptyNote({ text }) {
  return <p style={{ margin: 0, fontSize: 13, color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>{text}</p>;
}

function SectionCard({ title, icon, children }) {
  return (
    <div style={card}>
      <p style={sectionHead}>{icon && <span style={{ marginRight: 7 }}>{icon}</span>}{title}</p>
      {children}
    </div>
  );
}

/* All configurable cluster fields for Patient — resolved against fieldSettings at render time */
const PATIENT_CLUSTER_FIELD_MAP = [
  { key: "cluster_id",               label: "Cluster ID",             get: (c) => c.cluster_id },
  { key: "score_code",               label: "Score Code",             get: (c) => c.score_code },
  { key: "score_full_name",          label: "Score Full Name",        get: (c) => c.score_full_name },
  { key: "user_facing_name",         label: "User-Facing Name",       get: (c) => c.user_facing_name },
  { key: "user_subtitle",            label: "User Subtitle",          get: (c) => c.user_subtitle },
  { key: "tile_name",                label: "Tile Name",              get: (c) => c.tile_name },
  { key: "clinical_scope",           label: "Clinical Scope",         get: (c) => c.clinical_scope },
  { key: "what_moves_the_score",     label: "What Moves The Score",   get: (c) => c.what_moves_the_score },
  { key: "specialist_type",          label: "Specialist Type",        get: (c) => c.specialist_type,          multiline: true },
  { key: "user_persona",             label: "User Persona",           get: (c) => c.user_persona },
  { key: "biomarkers_in_cluster",    label: "Biomarkers",             get: (c) => c.biomarkers_in_cluster != null ? `${c.biomarkers_in_cluster} tracked` : null },
  { key: "composite_formula",        label: "Composite Formula",      get: (c) => c.composite_formula },
  { key: "objective_weights",        label: "Objective Weights",      get: (c) => c.objective_weights },
  { key: "score_bands",              label: "Score Bands",            get: (c) => c.score_bands },
  { key: "score_intent",             label: "Score Intent",           get: (c) => c.score_intent },
  { key: "physician_oversight",      label: "Physician Oversight",    get: (c) => c.physician_oversight || null },
  { key: "status",                   label: "Status",                 get: (c) => c.status },
  { key: "subjective_slot",          label: "Subjective Slot",        get: (c) => c.subjective_slot },
  { key: "lab_or_vital_markers",     label: "Lab or Vital Markers",   get: (c) => c.lab_or_vital_markers },
  { key: "required_lab_markers",     label: "Required Lab Markers",   get: (c) => c.required_lab_markers },
];

const PATIENT_FIELD_DEFAULTS = {
  cluster_id: "hidden", score_code: "hidden", score_full_name: "L2", user_facing_name: "L1",
  user_subtitle: "L1", tile_name: "hidden", clinical_scope: "hidden", what_moves_the_score: "L2",
  specialist_type: "hidden", user_persona: "hidden", biomarkers_in_cluster: "L2",
  composite_formula: "hidden", objective_weights: "hidden", score_bands: "L1",
  score_intent: "L1", physician_oversight: "hidden", status: "hidden",
  subjective_slot: "hidden", lab_or_vital_markers: "hidden", required_lab_markers: "hidden",
};

/* Default biomarker field visibility for Patient (from image spec) */
const PATIENT_BIO_FIELD_DEFAULTS = {
  backend_field: "hidden", data_type: "L2", score_direction: "hidden",
  scoring_type: "hidden", provenance: "hidden", clinical_rationale: "L2",
};

/* ClusterCard — single cluster with user L1 visible, L2 expandable (dynamic via fieldSettings) */
function ClusterCard({ cluster, fieldSettings, biomarkerFieldSettings }) {
  const [open, setOpen] = useState(false);
  const [bmOpen, setBmOpen] = useState(false);
  const [biomarkers, setBiomarkers] = useState(null);
  const cl = cluster;
  const clColor = CLUSTER_COLORS[cl.cluster_code?.toLowerCase()] || CLUSTER_COLORS.longevity;
  const settings = Object.keys(fieldSettings || {}).length > 0 ? fieldSettings : PATIENT_FIELD_DEFAULTS;
  const bfSettings = Object.keys(biomarkerFieldSettings || {}).length > 0 ? biomarkerFieldSettings : PATIENT_BIO_FIELD_DEFAULTS;
  // L1 fields (always visible to user)
  const l1 = PATIENT_CLUSTER_FIELD_MAP
    .filter(({ key }) => settings[key] === "L1")
    .map(({ label, get }) => ({ label, val: get(cl) }))
    .filter(({ val }) => val != null && val !== "");
  // L2 fields (expandable)
  const l2 = PATIENT_CLUSTER_FIELD_MAP
    .filter(({ key }) => settings[key] === "L2")
    .map(({ label, get }) => ({ label, val: get(cl) }))
    .filter(({ val }) => val != null && val !== "");
  // Biomarker L2 fields (what would show for biomarkers in this cluster)
  const bioL2Labels = [];
  if (bfSettings.data_type === "L2") bioL2Labels.push("Data Type");
  if (bfSettings.clinical_rationale === "L2") bioL2Labels.push("Clinical Rationale");
  return (
    <div style={{ border: "1px solid rgba(90,144,168,0.18)", borderRadius: 14, overflow: "hidden", background: "#FFFFFF", boxShadow: "0 1px 8px rgba(90,144,168,0.07)" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 18px", background: "rgba(90,144,168,0.025)" }}>
        <div style={{ width: 44, height: 44, borderRadius: 10, overflow: "hidden", flexShrink: 0, border: "1px solid rgba(90,144,168,0.2)", background: "rgba(90,144,168,0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {cl.icon_url
            ? <img src={cl.icon_url.startsWith("http") ? cl.icon_url : `${UPLOADS_BASE}${cl.icon_url}`} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <span style={{ fontSize: 20 }}>🧬</span>}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>
            {cl.user_facing_name || cl.cluster_name}
          </p>
          {cl.user_subtitle && <p style={{ margin: "2px 0 0", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>{cl.user_subtitle}</p>}
        </div>
        <span style={{ padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, fontFamily: "Inter, sans-serif", background: `${clColor.color}18`, color: clColor.color, border: `1px solid ${clColor.color}30`, whiteSpace: "nowrap", textTransform: "capitalize" }}>
          {cl.cluster_group?.replace(/_/g, " ") || "Cluster"}
        </span>
      </div>
      {/* L1 fields */}
      {l1.length > 0 && (
        <div style={{ padding: "12px 18px 0" }}>
          {l1.map(({ label, val }) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, padding: "7px 0", borderBottom: "1px solid rgba(90,144,168,0.08)" }}>
              <span style={{ fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif", flexShrink: 0, minWidth: 120 }}>{label}</span>
              <span style={{ fontSize: 12, color: "#1C3545", fontFamily: "Inter, sans-serif", textAlign: "right", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{val}</span>
            </div>
          ))}
        </div>
      )}
      {/* L2 expand */}
      {(l2.length > 0 || bioL2Labels.length > 0) && (
        <div style={{ padding: "8px 18px 14px" }}>
          <button onClick={() => setOpen((p) => !p)}
            style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", color: "#5A90A8", fontSize: 12, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer", padding: "4px 0", marginTop: 4 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 200ms" }}><polyline points="6 9 12 15 18 9"/></svg>
            {open ? "Show Less" : `More Details (${l2.length + (bioL2Labels.length > 0 ? 1 : 0)})`}
          </button>
          {open && (
            <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px dashed rgba(90,144,168,0.18)" }}>
              {l2.map(({ label, val }) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, padding: "6px 0", borderBottom: "1px solid rgba(90,144,168,0.06)" }}>
                  <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif", flexShrink: 0, minWidth: 120 }}>{label}</span>
                  <span style={{ fontSize: 11, color: "#4D7A8E", fontFamily: "Inter, sans-serif", textAlign: "right", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{val}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {/* Biomarker section — lazy loaded */}
      {cl.biomarkers_in_cluster > 0 && (
        <div style={{ padding: "6px 18px 14px" }}>
          <button onClick={async () => {
            if (!bmOpen && biomarkers === null) {
              try {
                const { data } = await api.get(`/patient/clusters/${cl.id}/biomarkers`);
                setBiomarkers(Array.isArray(data) ? data : []);
              } catch { setBiomarkers([]); }
            }
            setBmOpen((p) => !p);
          }} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", color: "#5A90A8", fontSize: 12, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer", padding: "4px 0" }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" style={{ transform: bmOpen ? "rotate(180deg)" : "none", transition: "transform 200ms" }}><polyline points="6 9 12 15 18 9"/></svg>
            {bmOpen ? "Hide Biomarkers" : `Biomarkers (${cl.biomarkers_in_cluster})`}
          </button>
          {bmOpen && biomarkers !== null && (
            <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
              {biomarkers.length === 0 ? (
                <p style={{ margin: 0, fontSize: 12, color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>No biomarker data available.</p>
              ) : biomarkers.map((bm) => (
                <div key={bm.id} style={{ padding: "10px 12px", borderRadius: 8, background: "rgba(90,144,168,0.04)", border: "1px solid rgba(90,144,168,0.12)" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 4 }}>
                    <div>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{bm.biomarker_name}</span>
                      <code style={{ marginLeft: 8, fontSize: 10, color: "#5A90A8", background: "rgba(90,144,168,0.1)", padding: "1px 5px", borderRadius: 4, fontFamily: "monospace" }}>{bm.biomarker_code}</code>
                    </div>
                    <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 999, background: "rgba(90,144,168,0.08)", color: "#4D7A8E", fontFamily: "Inter, sans-serif", textTransform: "capitalize", flexShrink: 0 }}>{bm.category}</span>
                  </div>
                  {bm.description && <p style={{ margin: "0 0 6px", fontSize: 11, color: "#7AAABB", fontFamily: "Inter, sans-serif", lineHeight: 1.5 }}>{bm.description}</p>}
                  {/* L2 biomarker fields for patient */}
                  {((bfSettings.data_type === "L2" && bm.data_type?.length > 0) || (bfSettings.clinical_rationale === "L2" && bm.clinical_rationale)) ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 4, paddingTop: 8, borderTop: "1px solid rgba(90,144,168,0.1)" }}>
                      {bfSettings.data_type === "L2" && bm.data_type?.length > 0 && (
                        <div>
                          <p style={{ margin: "0 0 3px", fontSize: 10, color: "#9BBCCC", fontFamily: "Inter, sans-serif", textTransform: "uppercase", letterSpacing: "0.06em" }}>Data Type</p>
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                            {bm.data_type.map((t) => <span key={t} style={{ fontSize: 10, padding: "1px 6px", borderRadius: 999, background: "rgba(90,144,168,0.1)", border: "1px solid rgba(90,144,168,0.2)", color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>{t}</span>)}
                          </div>
                        </div>
                      )}
                      {bfSettings.clinical_rationale === "L2" && bm.clinical_rationale && (
                        <div style={{ flex: "1 1 200px" }}>
                          <p style={{ margin: "0 0 3px", fontSize: 10, color: "#9BBCCC", fontFamily: "Inter, sans-serif", textTransform: "uppercase", letterSpacing: "0.06em" }}>Clinical Rationale</p>
                          <p style={{ margin: 0, fontSize: 11, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.5 }}>{bm.clinical_rationale}</p>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {l1.length === 0 && l2.length === 0 && bioL2Labels.length === 0 && cl.biomarkers_in_cluster === 0 && (
        <div style={{ padding: "12px 18px 16px" }}>
          <p style={{ margin: 0, fontSize: 13, color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>{cl.description || "No additional information available."}</p>
        </div>
      )}
    </div>
  );
}

function Spinner({ label = "Loading your dashboard…" }) {
  return (
    <div style={{ minHeight: "100vh", background: "#EDF4F7", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
      <div style={{ width: 40, height: 40, border: "3px solid rgba(90,144,168,0.2)", borderTopColor: "#8AAEBB", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <p style={{ color: "#7AAABB", fontSize: 14, fontFamily: "Inter, sans-serif", margin: 0 }}>{label}</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function StatCard({ value, label, color = "#5A90A8" }) {
  return (
    <div style={{ flex: "1 1 140px", padding: "20px 22px", borderRadius: 16, background: "#FFFFFF", border: "1px solid rgba(90,144,168,0.18)", boxShadow: "0 2px 8px rgba(90,144,168,0.08)" }}>
      <p style={{ margin: "0 0 4px", fontSize: 28, fontWeight: 700, color, fontFamily: "Inter, sans-serif", lineHeight: 1 }}>{value}</p>
      <p style={{ margin: 0, fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif", textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</p>
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────────────────────────── */
export default function UserDashboard({ onLogout }) {
  const { user, logout } = useAuth();
  const [profile,  setProfile]  = useState(null);
  const [doctors,  setDoctors]  = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [dashView, setDashView] = useState("home");
  const [clusterCatalog, setClusterCatalog] = useState([]);
  const [clusterFieldSettings, setClusterFieldSettings] = useState({});
  const [biomarkerFieldSettings, setBiomarkerFieldSettings] = useState({});

  /* Find Care (marketplace) state */
  const [searchQ,  setSearchQ]  = useState("");
  const [results,   setResults]   = useState(null);
  const [searching, setSearching] = useState(false);
  const [busyId,    setBusyId]    = useState(null);
  const [careMsg,   setCareMsg]   = useState(null);
  const [enrolledProgramIds, setEnrolledProgramIds] = useState(new Set());

  const refreshMyCare = async () => {
    const [docsRes, progRes] = await Promise.allSettled([
      api.get("/patient/doctors"),
      api.get("/patient/programs"),
    ]);
    if (docsRes.status === "fulfilled") setDoctors(Array.isArray(docsRes.value.data) ? docsRes.value.data : []);
    if (progRes.status === "fulfilled") setPrograms(Array.isArray(progRes.value.data) ? progRes.value.data : []);
  };

  useEffect(() => {
    const load = async () => {
      const [profRes, docsRes, progRes, clustRes, cfvRes, bfvRes] = await Promise.allSettled([
        api.get("/patient/profile"),
        api.get("/patient/doctors"),
        api.get("/patient/programs"),
        api.get("/patient/clusters/catalog"),
        api.get("/patient/clusters/field-settings"),
        api.get("/patient/clusters/biomarker-field-settings"),
      ]);
      if (profRes.status   === "fulfilled") setProfile(profRes.value.data);
      if (docsRes.status   === "fulfilled") setDoctors(Array.isArray(docsRes.value.data) ? docsRes.value.data : []);
      if (progRes.status   === "fulfilled") setPrograms(Array.isArray(progRes.value.data) ? progRes.value.data : []);
      if (clustRes.status  === "fulfilled") setClusterCatalog(Array.isArray(clustRes.value.data) ? clustRes.value.data : []);
      if (cfvRes.status    === "fulfilled" && cfvRes.value.data && typeof cfvRes.value.data === "object") setClusterFieldSettings(cfvRes.value.data);
      if (bfvRes.status    === "fulfilled" && bfvRes.value.data && typeof bfvRes.value.data === "object") setBiomarkerFieldSettings(bfvRes.value.data);
      setLoading(false);
    };
    load();
  }, []);

  const runSearch = async (q) => {
    // Results are shown only after an actual search — an empty query
    // returns the view to its initial prompt state.
    if (!(q || "").trim()) {
      setResults(null);
      setCareMsg(null);
      return;
    }
    setSearching(true);
    setCareMsg(null);
    try {
      const { data } = await api.get("/patient/discovery/search", { params: { q } });
      setResults(data);
    } catch {
      setCareMsg({ type: "error", text: "Search failed. Please try again." });
    }
    setSearching(false);
  };

  const handleEnroll = async (program) => {
    setBusyId(program.id);
    setCareMsg(null);
    try {
      const { data } = await api.post(`/patient/discovery/programs/${program.id}/enroll`);
      setEnrolledProgramIds((prev) => new Set(prev).add(program.id));
      setCareMsg({ type: "success", text: `You're enrolled in "${data.program_name}" with ${data.doctor_name}.` });
      await refreshMyCare();
    } catch (err) {
      setCareMsg({ type: "error", text: err.response?.data?.detail || "Could not enroll. Please try again." });
    }
    setBusyId(null);
  };

  const handleConnect = async (doc) => {
    setBusyId(doc.id);
    setCareMsg(null);
    try {
      const { data } = await api.post(`/patient/discovery/doctors/${doc.id}/connect`);
      setCareMsg({ type: "success", text: `You're now connected with ${data.doctor_name}.` });
      await refreshMyCare();
    } catch (err) {
      setCareMsg({ type: "error", text: err.response?.data?.detail || "Could not connect. Please try again." });
    }
    setBusyId(null);
  };

  const handleLogout = async () => {
    await logout();
    onLogout();
  };

  if (loading) return <Spinner />;

  const displayName = profile?.full_name || user?.name || "Member";
  const initials = displayName.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const avatarSrc = profile?.avatar_url
    ? (profile.avatar_url.startsWith("http") ? profile.avatar_url : `${UPLOADS_BASE}${profile.avatar_url}`)
    : null;

  const lifestyle = profile?.lifestyle;
  const health    = profile?.health_history;

  /* ─── Header ─────────────────────────────────────────────────────────────── */
  const Header = (
    <header className="ud-header" style={{ position: "sticky", top: 0, zIndex: 50, background: "#8AAEBB", borderBottom: "1px solid rgba(90,144,168,0.35)", padding: "0 28px", height: 58, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <span className="font-serif-display" style={{ fontSize: 17, fontWeight: 700, color: "#FFFFFF", letterSpacing: "-0.035em" }}>
        Supercore Longevity
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <button
        className="ud-findcare"
        onClick={() => setDashView("find")}
        style={{ padding: "6px 16px", borderRadius: 8, border: dashView === "find" ? "1px solid rgba(255,255,255,0.85)" : "1px solid rgba(255,255,255,0.45)", background: dashView === "find" ? "rgba(255,255,255,0.18)" : "transparent", color: "rgba(255,255,255,0.92)", fontSize: 13, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer", transition: "all 150ms ease", whiteSpace: "nowrap" }}
        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.15)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = dashView === "find" ? "rgba(255,255,255,0.18)" : "transparent"; }}
      >
        🔍 Find Care
      </button>
      <button
        onClick={() => setDashView("profile")}
        style={{ display: "flex", alignItems: "center", gap: 10, background: "transparent", border: "none", cursor: "pointer", padding: "6px 10px", borderRadius: 10, transition: "background 150ms ease" }}
        onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
        title="View profile"
      >
        <div style={{ width: 32, height: 32, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.55)", overflow: "hidden", flexShrink: 0, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {avatarSrc
            ? <img src={avatarSrc} alt={displayName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <span style={{ fontSize: 12, fontWeight: 700, color: "#FFFFFF", fontFamily: "Inter, sans-serif" }}>{initials}</span>
          }
        </div>
        <span className="ud-username" style={{ fontSize: 13, fontWeight: 500, color: "rgba(255,255,255,0.92)", fontFamily: "Inter, sans-serif", whiteSpace: "nowrap" }}>
          {displayName}
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
      </button>
      <button
        className="ud-logout"
        onClick={handleLogout}
        style={{ padding: "6px 16px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.45)", background: "transparent", color: "rgba(255,255,255,0.88)", fontSize: 13, fontFamily: "Inter, sans-serif", cursor: "pointer", transition: "all 150ms ease" }}
        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.15)"; e.currentTarget.style.color = "#FFF"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "rgba(255,255,255,0.88)"; }}
      >
        Log out
      </button>
      </div>
    </header>
  );

  /* ─── Home overview ─────────────────────────────────────────────────────── */
  const HomeView = (
    <main style={{ position: "relative", zIndex: 1, maxWidth: 980, margin: "0 auto", padding: "36px 24px 80px" }}>

      {/* Welcome */}
      <div style={{ marginBottom: 32 }}>
        <p style={{ margin: "0 0 4px", fontSize: 13, color: "#5A90A8", fontFamily: "Inter, sans-serif", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>Welcome back</p>
        <h2 style={{ margin: "0 0 6px", fontSize: 28, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", letterSpacing: "-0.025em" }}>
          {displayName}
        </h2>
        <p style={{ margin: 0, fontSize: 14, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
          {profile?.member_since
            ? `Member since ${new Date(profile.member_since).toLocaleDateString("en-US", { month: "long", year: "numeric" })}`
            : "Your longevity journey starts here."}
        </p>
      </div>

      {/* Find Care CTA */}
      <div style={{ ...card, marginBottom: 28, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap", background: "linear-gradient(135deg, #FFFFFF 0%, #EAF3F7 100%)" }}>
        <div style={{ flex: "1 1 280px" }}>
          <p style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>Find your care</p>
          <p style={{ margin: 0, fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif", lineHeight: 1.5 }}>
            Search doctors and programs by name, specialty, or health focus — e.g. “Immunity & Inflammation”.
          </p>
        </div>
        <button
          onClick={() => setDashView("find")}
          style={{ padding: "10px 22px", borderRadius: 10, background: "#5A90A8", border: "none", color: "#FFFFFF", fontSize: 13, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer", whiteSpace: "nowrap", transition: "background 150ms ease" }}
          onMouseEnter={(e) => e.currentTarget.style.background = "#4D7A8E"}
          onMouseLeave={(e) => e.currentTarget.style.background = "#5A90A8"}
        >
          🔍 Find Doctors & Programs
        </button>
      </div>

      {/* Stats row */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 28 }}>
        {profile?.longevity_score != null && (
          <StatCard value={profile.longevity_score} label="Longevity Score" color="#5A90A8" />
        )}
        {profile?.biological_age != null && (
          <StatCard value={`${profile.biological_age}y`} label="Biological Age" color="#16A34A" />
        )}
        <StatCard value={doctors.length || "—"} label="My Doctors" color="#2563EB" />
        <StatCard value={programs.length || "—"} label="Programs" color="#7C3AED" />
      </div>

      {/* Quick info grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>

        {/* Profile summary */}
        <div style={card}>
          <p style={sectionHead}>Your Profile</p>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 18 }}>
            <div style={{ width: 52, height: 52, borderRadius: "50%", border: "2px solid rgba(90,144,168,0.4)", overflow: "hidden", flexShrink: 0, background: "rgba(90,144,168,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {avatarSrc
                ? <img src={avatarSrc} alt={displayName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : <span style={{ fontSize: 16, fontWeight: 700, color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>{initials}</span>
              }
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{displayName}</p>
              {user?.email && <p style={{ margin: "2px 0 0", fontSize: 12, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>{user.email}</p>}
            </div>
          </div>
          {profile?.city || profile?.country ? (
            <p style={{ margin: "0 0 4px", fontSize: 13, color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>
              📍 {[profile.city, profile.country].filter(Boolean).join(", ")}
            </p>
          ) : null}
          {profile?.gender && (
            <p style={{ margin: "0 0 4px", fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
              {GENDER_LABELS[profile.gender] ?? profile.gender}
            </p>
          )}
          <button
            onClick={() => setDashView("profile")}
            style={{ marginTop: 14, padding: "8px 18px", borderRadius: 8, background: "rgba(90,144,168,0.1)", border: "1px solid rgba(90,144,168,0.25)", color: "#5A90A8", fontSize: 13, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer" }}
            onMouseEnter={(e) => e.currentTarget.style.background = "rgba(90,144,168,0.18)"}
            onMouseLeave={(e) => e.currentTarget.style.background = "rgba(90,144,168,0.1)"}
          >
            View full profile →
          </button>
        </div>

        {/* Lifestyle snapshot */}
        <div style={card}>
          <p style={sectionHead}>Lifestyle Snapshot</p>
          {lifestyle ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {lifestyle.diet_type && <InfoRow label="Diet" value={DIET_LABELS[lifestyle.diet_type] ?? lifestyle.diet_type} />}
              {lifestyle.exercise_frequency && <InfoRow label="Exercise" value={EXERCISE_FREQ_LABELS[lifestyle.exercise_frequency] ?? lifestyle.exercise_frequency} />}
              {lifestyle.sleep_hours_avg != null && <InfoRow label="Sleep" value={`${lifestyle.sleep_hours_avg}h avg`} />}
              {lifestyle.stress_level && <InfoRow label="Stress" value={STRESS_LABELS[lifestyle.stress_level] ?? lifestyle.stress_level} />}
              {lifestyle.smoking_status && <InfoRow label="Smoking" value={SMOKING_LABELS[lifestyle.smoking_status] ?? lifestyle.smoking_status} />}
              {!lifestyle.diet_type && !lifestyle.exercise_frequency && !lifestyle.sleep_hours_avg && (
                <EmptyNote text="No lifestyle data recorded yet." />
              )}
            </div>
          ) : (
            <EmptyNote text="Complete onboarding to see your lifestyle data here." />
          )}
        </div>

        {/* Health summary */}
        <div style={card}>
          <p style={sectionHead}>Health Summary</p>
          {health ? (
            <div>
              {health.existing_conditions?.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <p style={{ margin: "0 0 6px", fontSize: 11, fontWeight: 600, color: "#9BBCCC", letterSpacing: "0.06em", textTransform: "uppercase", fontFamily: "Inter, sans-serif" }}>Conditions</p>
                  <div style={{ display: "flex", flexWrap: "wrap" }}>
                    {health.existing_conditions.map((c, i) => <Tag key={i}>{c}</Tag>)}
                  </div>
                </div>
              )}
              {health.current_medications && (
                <div style={{ marginBottom: 12 }}>
                  <p style={{ margin: "0 0 6px", fontSize: 11, fontWeight: 600, color: "#9BBCCC", letterSpacing: "0.06em", textTransform: "uppercase", fontFamily: "Inter, sans-serif" }}>Medications</p>
                  <p style={{ margin: 0, fontSize: 13, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.6 }}>{health.current_medications}</p>
                </div>
              )}
              {health.allergies && (
                <div>
                  <p style={{ margin: "0 0 6px", fontSize: 11, fontWeight: 600, color: "#9BBCCC", letterSpacing: "0.06em", textTransform: "uppercase", fontFamily: "Inter, sans-serif" }}>Allergies</p>
                  <p style={{ margin: 0, fontSize: 13, color: "#DC2626", fontFamily: "Inter, sans-serif", lineHeight: 1.6 }}>{health.allergies}</p>
                </div>
              )}
              {!health.existing_conditions?.length && !health.current_medications && !health.allergies && (
                <EmptyNote text="No health history recorded yet." />
              )}
            </div>
          ) : (
            <EmptyNote text="Complete onboarding to see your health data here." />
          )}
        </div>

        {/* Longevity Clusters */}
        <div style={card}>
          <p style={sectionHead}>Longevity Clusters</p>
          {clusterCatalog.length > 0 ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
              {clusterCatalog.map((cl) => <ClusterCard key={cl.id} cluster={cl} fieldSettings={clusterFieldSettings} biomarkerFieldSettings={biomarkerFieldSettings} />)}
            </div>
          ) : profile?.health_clusters?.length ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {profile.health_clusters.map((cluster, i) => {
                const s = CLUSTER_COLORS[cluster?.toLowerCase()] || CLUSTER_COLORS.longevity;
                return <Tag key={i} color={s.color}>{cluster}</Tag>;
              })}
            </div>
          ) : (
            <EmptyNote text="No health clusters assigned yet. Your care team will set these after your initial assessment." />
          )}
        </div>

        {/* My Doctors */}
        <div style={card}>
          <p style={sectionHead}>My Doctors</p>
          {doctors.length === 0 ? (
            <div>
              <EmptyNote text="No doctors connected yet." />
              <button
                onClick={() => setDashView("find")}
                style={{ marginTop: 12, padding: "8px 18px", borderRadius: 8, background: "rgba(90,144,168,0.1)", border: "1px solid rgba(90,144,168,0.25)", color: "#5A90A8", fontSize: 13, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer" }}
                onMouseEnter={(e) => e.currentTarget.style.background = "rgba(90,144,168,0.18)"}
                onMouseLeave={(e) => e.currentTarget.style.background = "rgba(90,144,168,0.1)"}
              >
                Find a doctor →
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {doctors.slice(0, 3).map((doc, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(90,144,168,0.1)", border: "1px solid rgba(90,144,168,0.22)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {doc.profile_photo_url
                      ? <img src={doc.profile_photo_url.startsWith("http") ? doc.profile_photo_url : `${UPLOADS_BASE}${doc.profile_photo_url}`} alt={doc.display_name} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />
                      : <span style={{ fontSize: 12, fontWeight: 700, color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>
                          {(doc.display_name || "Dr").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                        </span>
                    }
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.display_name}</p>
                    {doc.primary_specialty && <p style={{ margin: "2px 0 0", fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>{doc.primary_specialty}</p>}
                  </div>
                </div>
              ))}
              {doctors.length > 3 && <p style={{ margin: "4px 0 0", fontSize: 12, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>+{doctors.length - 3} more</p>}
            </div>
          )}
        </div>

        {/* Programs */}
        <div style={card}>
          <p style={sectionHead}>Active Programs</p>
          {programs.length === 0 ? (
            <div>
              <EmptyNote text="No programs enrolled yet." />
              <button
                onClick={() => setDashView("find")}
                style={{ marginTop: 12, padding: "8px 18px", borderRadius: 8, background: "rgba(90,144,168,0.1)", border: "1px solid rgba(90,144,168,0.25)", color: "#5A90A8", fontSize: 13, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer" }}
                onMouseEnter={(e) => e.currentTarget.style.background = "rgba(90,144,168,0.18)"}
                onMouseLeave={(e) => e.currentTarget.style.background = "rgba(90,144,168,0.1)"}
              >
                Browse programs →
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {programs.slice(0, 4).map((prog, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 13, color: "#2D5A72", fontFamily: "Inter, sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                    {prog.name || `Program ${i + 1}`}
                  </span>
                  {prog.status && (
                    <span style={{ flexShrink: 0, padding: "2px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, fontFamily: "Inter, sans-serif", textTransform: "capitalize", color: prog.status === "active" ? "#16A34A" : "#7AAABB", background: prog.status === "active" ? "rgba(22,163,74,0.1)" : "rgba(90,144,168,0.06)", border: `1px solid ${prog.status === "active" ? "rgba(22,163,74,0.25)" : "rgba(90,144,168,0.15)"}` }}>
                      {prog.status}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );

  /* ─── Profile detail view ───────────────────────────────────────────────── */
  const ProfileView = (
    <main style={{ position: "relative", zIndex: 1, maxWidth: 980, margin: "0 auto", padding: "36px 24px 80px" }}>

      {/* Back + title */}
      <div style={{ marginBottom: 28, display: "flex", alignItems: "center", gap: 14 }}>
        <button
          onClick={() => setDashView("home")}
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.25)", background: "rgba(90,144,168,0.08)", color: "#4D7A8E", fontSize: 13, fontFamily: "Inter, sans-serif", cursor: "pointer" }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(90,144,168,0.45)"; e.currentTarget.style.color = "#1C3545"; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(90,144,168,0.25)"; e.currentTarget.style.color = "#4D7A8E"; }}
        >
          ← Back
        </button>
        <div>
          <h2 style={{ margin: "0 0 2px", fontSize: 20, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", letterSpacing: "-0.02em" }}>My Profile</h2>
          <p style={{ margin: 0, fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Personal information, lifestyle, and health history</p>
        </div>
      </div>

      {/* Hero card */}
      <div style={{ ...card, padding: "32px 32px 28px", marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ width: 80, height: 80, borderRadius: "50%", border: "3px solid rgba(90,144,168,0.4)", overflow: "hidden", flexShrink: 0, background: "rgba(90,144,168,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {avatarSrc
              ? <img src={avatarSrc} alt={displayName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <span style={{ fontSize: 24, fontWeight: 700, color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>{initials}</span>
            }
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <h1 style={{ margin: "0 0 4px", fontSize: 24, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", letterSpacing: "-0.025em" }}>
              {displayName}
            </h1>
            {user?.email && <p style={{ margin: "0 0 4px", fontSize: 14, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>{user.email}</p>}
            {profile?.member_since && (
              <p style={{ margin: 0, fontSize: 12, color: "#A8C8D8", fontFamily: "Inter, sans-serif" }}>
                Member since {new Date(profile.member_since).toLocaleDateString("en-US", { month: "long", year: "numeric" })}
              </p>
            )}
          </div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
            {profile?.longevity_score != null && (
              <div style={{ textAlign: "center", padding: "10px 18px", borderRadius: 14, background: "rgba(90,144,168,0.08)", border: "1px solid rgba(90,144,168,0.2)" }}>
                <p style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#5A90A8", fontFamily: "Inter, sans-serif", lineHeight: 1 }}>{profile.longevity_score}</p>
                <p style={{ margin: "4px 0 0", fontSize: 10, color: "#9BBCCC", fontFamily: "Inter, sans-serif", textTransform: "uppercase", letterSpacing: "0.08em" }}>Score</p>
              </div>
            )}
            {profile?.biological_age != null && (
              <div style={{ textAlign: "center", padding: "10px 18px", borderRadius: 14, background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.2)" }}>
                <p style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#16A34A", fontFamily: "Inter, sans-serif", lineHeight: 1 }}>{profile.biological_age}</p>
                <p style={{ margin: "4px 0 0", fontSize: 10, color: "#9BBCCC", fontFamily: "Inter, sans-serif", textTransform: "uppercase", letterSpacing: "0.08em" }}>Bio Age</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 20 }}>

        {/* Personal Information */}
        <SectionCard title="Personal Information" icon="👤">
          <InfoRow label="Full Name"      value={profile?.full_name || user?.name} />
          <InfoRow label="Email"          value={user?.email} />
          <InfoRow label="Phone"          value={profile?.phone} />
          <InfoRow label="Date of Birth"  value={profile?.date_of_birth} />
          <InfoRow label="Gender"         value={profile?.gender ? (GENDER_LABELS[profile.gender] ?? profile.gender) : null} />
          <InfoRow label="Location"       value={[profile?.city, profile?.country].filter(Boolean).join(", ") || null} />
          <InfoRow label="Timezone"       value={profile?.timezone} />
          {!profile?.phone && !profile?.date_of_birth && !profile?.gender && (
            <EmptyNote text="Complete your personal info to personalise your experience." />
          )}
        </SectionCard>

        {/* Lifestyle & Habits */}
        <SectionCard title="Lifestyle & Habits" icon="🏃">
          {lifestyle ? (
            <>
              <InfoRow label="Occupation"   value={lifestyle.occupation} />
              <InfoRow label="Work Type"    value={lifestyle.work_type ? (WORK_TYPE_LABELS[lifestyle.work_type] ?? lifestyle.work_type) : null} />
              <InfoRow label="Diet"         value={lifestyle.diet_type ? (DIET_LABELS[lifestyle.diet_type] ?? lifestyle.diet_type) : null} />
              {lifestyle.diet_notes && (
                <div style={{ padding: "9px 0", borderBottom: "1px solid rgba(90,144,168,0.08)" }}>
                  <p style={{ margin: "0 0 4px", ...rowLabel }}>Diet Notes</p>
                  <p style={{ margin: 0, fontSize: 13, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.55 }}>{lifestyle.diet_notes}</p>
                </div>
              )}
              <InfoRow label="Water Intake"     value={lifestyle.water_intake_litres != null ? `${lifestyle.water_intake_litres}L / day` : null} />
              <InfoRow label="Sleep"            value={lifestyle.sleep_hours_avg != null ? `${lifestyle.sleep_hours_avg}h avg` : null} />
              <InfoRow label="Sleep Quality"    value={lifestyle.sleep_quality ? (SLEEP_QUALITY_LABELS[lifestyle.sleep_quality] ?? lifestyle.sleep_quality) : null} />
              <InfoRow label="Exercise"         value={lifestyle.exercise_frequency ? (EXERCISE_FREQ_LABELS[lifestyle.exercise_frequency] ?? lifestyle.exercise_frequency) : null} />
              <InfoRow label="Exercise Types"   tags={lifestyle.exercise_types?.length ? lifestyle.exercise_types : null} />
              <InfoRow label="Stress Level"     value={lifestyle.stress_level ? (STRESS_LABELS[lifestyle.stress_level] ?? lifestyle.stress_level) : null} />
              <InfoRow label="Alcohol"          value={lifestyle.alcohol_consumption ? (ALCOHOL_LABELS[lifestyle.alcohol_consumption] ?? lifestyle.alcohol_consumption) : null} />
              <InfoRow label="Smoking"          value={lifestyle.smoking_status ? (SMOKING_LABELS[lifestyle.smoking_status] ?? lifestyle.smoking_status) : null} />
            </>
          ) : (
            <EmptyNote text="No lifestyle data recorded yet. Complete onboarding to add this." />
          )}
        </SectionCard>

        {/* Health History */}
        <SectionCard title="Health History" icon="🩺">
          {health ? (
            <>
              <InfoRow label="Conditions"       tags={health.existing_conditions?.length ? health.existing_conditions : null} />
              <InfoRow label="Family History"   tags={health.family_history?.length ? health.family_history : null} />
              {health.past_surgeries && (
                <div style={{ padding: "9px 0", borderBottom: "1px solid rgba(90,144,168,0.08)" }}>
                  <p style={{ margin: "0 0 4px", ...rowLabel }}>Past Surgeries</p>
                  <p style={{ margin: 0, fontSize: 13, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.55 }}>{health.past_surgeries}</p>
                </div>
              )}
              {health.current_medications && (
                <div style={{ padding: "9px 0", borderBottom: "1px solid rgba(90,144,168,0.08)" }}>
                  <p style={{ margin: "0 0 4px", ...rowLabel }}>Current Medications</p>
                  <p style={{ margin: 0, fontSize: 13, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.55 }}>{health.current_medications}</p>
                </div>
              )}
              {health.allergies && (
                <div style={{ padding: "9px 0", borderBottom: "1px solid rgba(90,144,168,0.08)" }}>
                  <p style={{ margin: "0 0 4px", ...rowLabel }}>Allergies</p>
                  <p style={{ margin: 0, fontSize: 13, color: "#DC2626", fontFamily: "Inter, sans-serif", lineHeight: 1.55 }}>{health.allergies}</p>
                </div>
              )}
              <InfoRow label="Hormonal Status"  value={health.hormonal_status ? (HORMONAL_LABELS[health.hormonal_status] ?? health.hormonal_status) : null} />
              {!health.existing_conditions?.length && !health.current_medications && !health.allergies && !health.past_surgeries && !health.family_history?.length && !health.hormonal_status && (
                <EmptyNote text="No health history recorded yet." />
              )}
            </>
          ) : (
            <EmptyNote text="No health history recorded yet. Complete onboarding to add this." />
          )}
        </SectionCard>

        {/* Longevity Clusters */}
        <SectionCard title="Longevity Clusters" icon="🧬">
          {clusterCatalog.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {clusterCatalog.map((cl) => <ClusterCard key={cl.id} cluster={cl} fieldSettings={clusterFieldSettings} biomarkerFieldSettings={biomarkerFieldSettings} />)}
            </div>
          ) : profile?.health_clusters?.length ? (
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              {profile.health_clusters.map((c, i) => {
                const s = CLUSTER_COLORS[c?.toLowerCase()] || CLUSTER_COLORS.longevity;
                return <Tag key={i} color={s.color}>{c}</Tag>;
              })}
            </div>
          ) : (
            <EmptyNote text="No health clusters assigned yet." />
          )}
        </SectionCard>
      </div>

      {/* My Care Team */}
      <div style={{ marginTop: 20 }}>
        <SectionCard title="My Care Team" icon="👨‍⚕️">
          {doctors.length === 0 ? (
            <p style={{ margin: 0, fontSize: 13, color: "#A8C8D8", fontFamily: "Inter, sans-serif", textAlign: "center", padding: "16px 0" }}>
              No doctors connected yet.
            </p>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12 }}>
              {doctors.map((doc, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderRadius: 14, border: "1px solid rgba(90,144,168,0.15)", background: "#F5FAFC" }}>
                  <div style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(90,144,168,0.1)", border: "1px solid rgba(90,144,168,0.22)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {doc.profile_photo_url
                      ? <img src={doc.profile_photo_url.startsWith("http") ? doc.profile_photo_url : `${UPLOADS_BASE}${doc.profile_photo_url}`} alt={doc.display_name} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />
                      : <span style={{ fontSize: 14, fontWeight: 700, color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>
                          {(doc.display_name || "Dr").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                        </span>
                    }
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{doc.display_name}</p>
                    {doc.primary_specialty && <p style={{ margin: "2px 0 0", fontSize: 12, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>{doc.primary_specialty}</p>}
                    {doc.telehealth_enabled && <span style={{ fontSize: 11, color: "#16A34A", fontFamily: "Inter, sans-serif" }}>Telehealth Available</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>

      {/* Programs */}
      {programs.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <SectionCard title="My Programs" icon="📋">
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {programs.map((prog, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "14px 18px", borderRadius: 14, border: "1px solid rgba(90,144,168,0.12)", background: "#F5FAFC" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>
                      {prog.name || `Program ${i + 1}`}
                    </p>
                    {prog.description && <p style={{ margin: "4px 0 0", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif", lineHeight: 1.5 }}>{prog.description}</p>}
                  </div>
                  {prog.status && (
                    <span style={{ flexShrink: 0, padding: "4px 14px", borderRadius: 999, fontSize: 11, fontWeight: 600, fontFamily: "Inter, sans-serif", textTransform: "capitalize", color: prog.status === "active" ? "#16A34A" : "#7AAABB", background: prog.status === "active" ? "rgba(22,163,74,0.1)" : "rgba(90,144,168,0.06)", border: `1px solid ${prog.status === "active" ? "rgba(22,163,74,0.25)" : "rgba(90,144,168,0.15)"}` }}>
                      {prog.status}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      )}
    </main>
  );

  /* ─── Find Care (marketplace) view ──────────────────────────────────────── */
  const connectedDoctorIds = new Set(doctors.map((d) => d.id));

  const actionBtn = (label, onClick, { disabled = false, done = false } = {}) => (
    <button
      onClick={onClick}
      disabled={disabled || done}
      style={{
        padding: "8px 18px", borderRadius: 8, flexShrink: 0,
        border: done ? "1px solid rgba(22,163,74,0.35)" : "1px solid #5A90A8",
        background: done ? "rgba(22,163,74,0.1)" : (disabled ? "rgba(90,144,168,0.35)" : "#5A90A8"),
        color: done ? "#16A34A" : "#FFFFFF",
        fontSize: 12, fontWeight: 600, fontFamily: "Inter, sans-serif",
        cursor: disabled || done ? "default" : "pointer", whiteSpace: "nowrap",
      }}
    >
      {label}
    </button>
  );

  const FindCareView = (
    <main style={{ position: "relative", zIndex: 1, maxWidth: 980, margin: "0 auto", padding: "36px 24px 80px" }}>

      {/* Back + title */}
      <div style={{ marginBottom: 24, display: "flex", alignItems: "center", gap: 14 }}>
        <button
          onClick={() => setDashView("home")}
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.25)", background: "rgba(90,144,168,0.08)", color: "#4D7A8E", fontSize: 13, fontFamily: "Inter, sans-serif", cursor: "pointer" }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(90,144,168,0.45)"; e.currentTarget.style.color = "#1C3545"; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(90,144,168,0.25)"; e.currentTarget.style.color = "#4D7A8E"; }}
        >
          ← Back
        </button>
        <div>
          <h2 style={{ margin: "0 0 2px", fontSize: 20, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", letterSpacing: "-0.02em" }}>Find Care</h2>
          <p style={{ margin: 0, fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Search doctors and programs by name, specialty, city, clinic, or health focus</p>
        </div>
      </div>

      {/* Search bar */}
      <form
        onSubmit={(e) => { e.preventDefault(); runSearch(searchQ); }}
        style={{ display: "flex", gap: 10, marginBottom: 24, flexWrap: "wrap" }}
      >
        <input
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          placeholder='Try "Immunity & Inflammation", a doctor, specialty, city, or clinic…'
          style={{ flex: "1 1 320px", padding: "12px 16px", borderRadius: 12, border: "1px solid rgba(90,144,168,0.3)", background: "#FFFFFF", color: "#1C3545", fontSize: 14, fontFamily: "Inter, sans-serif", outline: "none" }}
          onFocus={(e) => e.currentTarget.style.borderColor = "#5A90A8"}
          onBlur={(e) => e.currentTarget.style.borderColor = "rgba(90,144,168,0.3)"}
        />
        <button
          type="submit"
          disabled={searching}
          style={{ padding: "12px 26px", borderRadius: 12, background: searching ? "rgba(90,144,168,0.5)" : "#5A90A8", border: "none", color: "#FFFFFF", fontSize: 14, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: searching ? "default" : "pointer" }}
        >
          {searching ? "Searching…" : "Search"}
        </button>
      </form>

      {/* Status message */}
      {careMsg && (
        <div style={{ marginBottom: 20, padding: "12px 18px", borderRadius: 12, fontSize: 13, fontFamily: "Inter, sans-serif", background: careMsg.type === "success" ? "rgba(22,163,74,0.1)" : "rgba(220,38,38,0.08)", border: `1px solid ${careMsg.type === "success" ? "rgba(22,163,74,0.3)" : "rgba(220,38,38,0.25)"}`, color: careMsg.type === "success" ? "#16A34A" : "#DC2626" }}>
          {careMsg.text}
        </div>
      )}

      {!results && !searching && (
        <div style={{ ...card, textAlign: "center", padding: "48px 28px" }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🔍</div>
          <p style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>
            Search to find your care
          </p>
          <p style={{ margin: 0, fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif", lineHeight: 1.6 }}>
            Type a doctor name, specialty, city, clinic, program, or a health focus
            like “Immunity & Inflammation” to see matching doctors and programs.
          </p>
        </div>
      )}
      {searching && !results ? (
        <p style={{ fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Searching…</p>
      ) : results && (
        <>
          {/* Matched clusters note */}
          {results.matched_clusters?.length > 0 && (
            <p style={{ margin: "0 0 18px", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
              Showing results related to {results.matched_clusters.map((c) => `“${c.name}”`).join(", ")}
            </p>
          )}

          {/* Programs */}
          <SectionCard title={`Programs (${results.programs?.length || 0})`} icon="📋">
            {!results.programs?.length ? (
              <EmptyNote text="No programs match your search." />
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {results.programs.map((prog) => {
                  const enrolled = enrolledProgramIds.has(prog.id);
                  return (
                    <div key={prog.id} style={{ display: "flex", gap: 14, alignItems: "flex-start", padding: "16px 18px", borderRadius: 14, border: "1px solid rgba(90,144,168,0.15)", background: "#F5FAFC", flexWrap: "wrap" }}>
                      <div style={{ flex: "1 1 260px", minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{prog.name}</p>
                        {prog.tagline && <p style={{ margin: "2px 0 0", fontSize: 12, color: "#5A90A8", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>{prog.tagline}</p>}
                        <p style={{ margin: "6px 0 0", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
                          {prog.doctor?.display_name}{prog.doctor?.primary_specialty ? ` · ${prog.doctor.primary_specialty}` : ""}
                        </p>
                        {prog.description && (
                          <p style={{ margin: "8px 0 0", fontSize: 13, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.55 }}>{prog.description}</p>
                        )}
                        <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", alignItems: "center" }}>
                          {prog.clusters?.map((c) => <Tag key={c.id}>{c.name}</Tag>)}
                          {prog.duration_weeks && <Tag>{prog.duration_weeks} weeks</Tag>}
                          {prog.total_sessions && <Tag>{prog.total_sessions} sessions</Tag>}
                          {prog.spots_left != null && prog.spots_left <= 5 && <Tag color="#DC2626">{prog.spots_left} spots left</Tag>}
                        </div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8, flexShrink: 0 }}>
                        {prog.price_inr != null && (
                          <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>
                            ₹{Number(prog.price_inr).toLocaleString("en-IN")}
                            {prog.pricing_model === "subscription" && <span style={{ fontSize: 11, fontWeight: 500, color: "#9BBCCC" }}> /mo</span>}
                            {prog.pricing_model === "per_session" && <span style={{ fontSize: 11, fontWeight: 500, color: "#9BBCCC" }}> /session</span>}
                          </p>
                        )}
                        {actionBtn(
                          enrolled ? "Enrolled ✓" : (busyId === prog.id ? "Enrolling…" : "Enroll"),
                          () => handleEnroll(prog),
                          { disabled: busyId === prog.id, done: enrolled },
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </SectionCard>

          {/* Doctors */}
          <div style={{ marginTop: 20 }}>
            <SectionCard title={`Doctors (${results.doctors?.length || 0})`} icon="👨‍⚕️">
              {!results.doctors?.length ? (
                <EmptyNote text="No doctors match your search." />
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
                  {results.doctors.map((doc) => {
                    const connected = connectedDoctorIds.has(doc.id);
                    return (
                      <div key={doc.id} style={{ padding: "16px 18px", borderRadius: 14, border: "1px solid rgba(90,144,168,0.15)", background: "#F5FAFC", display: "flex", flexDirection: "column", gap: 10 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <div style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(90,144,168,0.1)", border: "1px solid rgba(90,144,168,0.22)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
                            {doc.profile_photo_url
                              ? <img src={doc.profile_photo_url.startsWith("http") ? doc.profile_photo_url : `${UPLOADS_BASE}${doc.profile_photo_url}`} alt={doc.display_name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              : <span style={{ fontSize: 14, fontWeight: 700, color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>
                                  {(doc.display_name || "Dr").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                                </span>
                            }
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{doc.display_name}</p>
                            <p style={{ margin: "2px 0 0", fontSize: 12, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>
                              {[doc.primary_specialty, doc.years_of_experience ? `${doc.years_of_experience} yrs` : null].filter(Boolean).join(" · ")}
                            </p>
                            {(doc.clinic_name || doc.city || doc.country) && (
                              <p style={{ margin: "2px 0 0", fontSize: 11, color: "#A8C8D8", fontFamily: "Inter, sans-serif" }}>
                                📍 {[doc.clinic_name, doc.city, doc.country].filter(Boolean).join(", ")}
                              </p>
                            )}
                          </div>
                        </div>
                        {doc.bio_short && <p style={{ margin: 0, fontSize: 12, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.5 }}>{doc.bio_short}</p>}
                        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center" }}>
                          {doc.clusters?.slice(0, 3).map((c) => <Tag key={c.id}>{c.name}</Tag>)}
                          {doc.telehealth_enabled && <Tag color="#16A34A">Telehealth</Tag>}
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginTop: "auto" }}>
                          <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>
                            {doc.live_program_count > 0 ? `${doc.live_program_count} program${doc.live_program_count > 1 ? "s" : ""}` : ""}
                          </span>
                          {actionBtn(
                            connected ? "Connected ✓" : (busyId === doc.id ? "Connecting…" : "Connect"),
                            () => handleConnect(doc),
                            { disabled: busyId === doc.id || (doc.accepting_new_patients === false), done: connected },
                          )}
                        </div>
                        {doc.accepting_new_patients === false && !connected && (
                          <p style={{ margin: 0, fontSize: 11, color: "#DC2626", fontFamily: "Inter, sans-serif" }}>Not accepting new patients right now</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </SectionCard>
          </div>
        </>
      )}
    </main>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#EDF4F7", position: "relative" }}>
      <style>{`
        @media (max-width: 640px) {
          .ud-header {
            padding: 0 16px !important;
            gap: 8px !important;
          }
          .ud-header span[style] {
            font-size: 14px !important;
          }
          .ud-username {
            display: none !important;
          }
          .ud-findcare, .ud-logout {
            padding: 6px 10px !important;
            font-size: 12px !important;
          }
        }
        @media (max-width: 480px) {
          .ud-header {
            padding: 0 12px !important;
            flex-wrap: wrap !important;
            height: auto !important;
            min-height: 58px !important;
            padding-top: 8px !important;
            padding-bottom: 8px !important;
          }
        }
      `}</style>
      {Header}
      {dashView === "home" ? HomeView : dashView === "find" ? FindCareView : ProfileView}
    </div>
  );
}
